import { uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import { Accordion, Form, Spinner, Stack, Table } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { BsCashCoin, BsCreditCard, BsFiletypePdf } from 'react-icons/bs';
import { FaArrowLeft } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';
import { SFCAccordion } from '../../../../../components/accordion/SFCAccordion';
import { Badge } from '../../../../../components/badge/Badge';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { Forbidden } from '../../../../../components/error/Forbidden';
import { DetailSection, LabelText } from '../../../../../components/generic';
import { IconLink } from '../../../../../components/iconLink/IconLink';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { InvoiceItems } from '../../../../../components/tables/invoiceItems';
import { SimpleTable } from '../../../../../components/tables/SimpleTable';
import { useToast } from '../../../../../context/ToastContext';
import { downloadInvoice } from '../../../../../services/api';
import {
  useDeleteInvoiceMutation,
  useGetInvoiceQuery,
  useSendInvoiceMutation,
  useWarnInvoiceMutation
} from '../../../../../services/endpoints/finance/invoice';
import { getCurrentUser } from '../../../../../services/helper';
import { Invoice, Payment } from '../../../../../types/finance.types';
import { invoiceSessionsColumns } from '../../../../../utils/consts/columnDefs/financeColumns';
import { revisionsColumns } from '../../../../../utils/consts/columnDefs/sessionColumns';
import { invoiceStatusColors } from '../../../../../utils/consts/finance/finance';
import { dateToUTC, formatDate } from '../../../../../utils/dateUtils';
import { getErrorMessage } from '../../../../../utils/utils';
import {
  getInvoiceContactManageLinks,
  getInvoiceContacts,
  getPreselectedInvoiceContactIds
} from '../../../../../utils/invoiceContactUtils';
import { userCanAdmin_Invoices } from './InvoicesView';
import CurrencyCell from "../../../../../components/tables/cellComponents/CurrencyCell";
const LabelTextList = ({ label, list }: { label: string; list: string[] }) => (
    <Stack
        direction="vertical"
        gap={1}
    >
    <span
        className="fw-semibold"
        style={{ color: 'rgba(94, 100, 105, .5)' }}
    >
      {label}
    </span>
      {list.map((text, index) => (
          <span
              key={index}
              className="text-secondary fw-semibold"
          >
        {text}
      </span>
      ))}
    </Stack>
);

export const InvoiceDetails = () => {
  const params = useParams();
  const currentUser = getCurrentUser();
  const canAccessInvoices = userCanAdmin_Invoices(currentUser);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, error, isLoading } = useGetInvoiceQuery(+params.id!, {
    skip: !canAccessInvoices
  });
  const [invoice, setInvoice] = useState<Invoice>();
  const [deleteInvoice] = useDeleteInvoiceMutation();
  const [sendInvoice] = useSendInvoiceMutation();
  const [warnInvoice] = useWarnInvoiceMutation();
  const [showModal, setShowModal] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);

  // const [currentCourse, setCurrentCourse] = useState<number | null>(null);
  const [checkedValues, setCheckedValues] = useState<string[]>([]);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading invoice', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setInvoice(data.invoice);

      const preselected = getPreselectedInvoiceContactIds(data.invoice);
      setCheckedValues(preselected);
    }
  }, [data]);

  if (!canAccessInvoices) {
    return <Forbidden />
  }

  if (isLoading) {
    return (
        <div className="w-100 vh-100 d-inline-flex">
          <Spinner
              variant="primary"
              className="m-auto"
          />
        </div>
    );
  }

  const handleConfirmDelete = async () => {
    try {
      if (!canAccessInvoices) throw Error("You don't have permission to access this resource");

      await deleteInvoice(+params.id!).unwrap();
      setShowModal(false);
      navigate('/admin/finance/invoices');
      addToast('Invoice deleted successfully', 'success');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = event.target;

    let checkValues = [...checkedValues];
    if (checked) {
      checkValues.push(id);
    } else {
      checkValues = checkValues.filter((value) => value !== id);
    }

    setCheckedValues(checkValues);
  };

  const sendInvoiceHandler = async () => {
    if (!canAccessInvoices) {
      addToast("You don't have permission to access this resource", 'error');
      return;
    }

    if (checkedValues.length === 0) {
      addToast('Select at least one email to send the invoice', 'error');
      return;
    }
    setDisabledButton(true);
    try {
      await sendInvoice({
        id: invoice?.id ?? 0,
        contact_ids: checkedValues
      }).unwrap();
      addToast('The invoice was sent', 'success');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    } finally {
      setDisabledButton(false);
    }
  };

  const warnInvoiceHandler = async () => {
    if (!canAccessInvoices) {
      addToast("You don't have permission to access this resource", 'error');
      return;
    }

    if (!checkedValues?.length) {
      addToast('Select at least one email to send the invoice', 'error');
      return;
    }
    setDisabledButton(true);
    try {
      await warnInvoice({
        id: invoice?.id ?? 0,
        contact_ids: checkedValues
      }).unwrap();
      addToast('The invoice was sent successfully', 'success');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    } finally {
      setDisabledButton(false);
    }
  };

  const handlePreviewInvoicePDF = async () => {
    downloadInvoice({ path: `invoices/generate-invoice/${params.id}` });
  };

  // A payment is "Complete" when it carries a processor transaction id (card
  // payments). Manually-recorded payments (check / cash / Invoice) never have
  // one and are still real money received — they show as "Recorded", not hidden.
  const paymentStatus = (payment: Payment): { label: string; color: string } => {
    if (payment.transaction_id) return { label: 'Complete', color: '#3BA55D' };
    return { label: 'Recorded', color: '#5E6469' };
  };

  const payments = (invoice?.payments ?? []) as Payment[];

  const accordion = (
      <SFCAccordion defaultActiveKey={['0', '1', '2', '3', '4', '5']}>
        <Accordion.Item eventKey="0">
          <Accordion.Header>Sessions</Accordion.Header>
          <Accordion.Body>
            <SimpleTable
                columns={invoiceSessionsColumns}
                data={invoice?.courses ?? []}
            />
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="1">
          <Accordion.Header>Venues</Accordion.Header>
          <Accordion.Body>
            <Stack gap={2}>
              {uniqBy(invoice?.courses, 'venue_id')?.map((course, idx) => (
                  <LinkCell
                      key={idx}
                      content={course.venue.title}
                      url={`/admin/places/venues/${course.venue.id}`}
                      state={{ returnTo: `/admin/finance/invoices/${invoice?.id}` }}
                      underline
                  />
              ))}
            </Stack>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="2">
          <Accordion.Header>Organizations</Accordion.Header>
          <Accordion.Body>
            <Stack gap={2}>
              {invoice?.courses?.map((course, idx) =>
                  course.venue.organizations.map((o) => (
                      <LinkCell
                          key={idx}
                          content={o.name}
                          url={`/admin/places/organizations/${o.id}`}
                          underline
                      />
                  ))
              )}
            </Stack>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="3">
          <Accordion.Header>Invoice Items</Accordion.Header>
          <Accordion.Body>
            <InvoiceItems
                data={invoice?.invoice_items ?? []}
                isPaidInvoice={invoice?.status === 'paid'}
            />
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="4">
          <Accordion.Header>Payments</Accordion.Header>
          <Accordion.Body>
            {payments.length > 0 ? (
                <Table
                    responsive
                    hover
                    className="mb-0"
                >
                  <thead>
                  <tr>
                    <th>ID</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Check #</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                  </thead>
                  <tbody>
                  {payments.map((payment) => {
                    const status = paymentStatus(payment);
                    return (
                        <tr key={payment.id}>
                          <td>{payment.id}</td>
                          <td><CurrencyCell value={payment.total ?? 0} /></td>
                          <td>{payment.payment_method ?? 'N/A'}</td>
                          <td>{payment.check_number ?? '—'}</td>
                          <td>
                            {payment.created_at
                                ? dateToUTC(new Date(payment.created_at), 'MM/dd/yyyy')
                                : '—'}
                          </td>
                          <td>
                            <Badge
                                title={status.label}
                                color={status.color}
                            />
                          </td>
                        </tr>
                    );
                  })}
                  </tbody>
                </Table>
            ) : (
                <span className="fst-italic">There are no payments to show.</span>
            )}
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="5">
          <Accordion.Header>Revisions</Accordion.Header>
          <Accordion.Body>
            <SimpleTable
                columns={revisionsColumns.filter((column) => column.header !== 'Comment')}
                data={invoice?.audits ?? []}
            />
          </Accordion.Body>
        </Accordion.Item>
      </SFCAccordion>
  );

  const invoiceContacts = getInvoiceContacts(invoice);
  const contactManageLinks = getInvoiceContactManageLinks(invoice);

  const invoiceDetails = (
      <>
        <DetailSection title="Details">
          <LabelText label="ID:">{invoice?.id}</LabelText>
          <LabelText label="Invoice Name:">{invoice?.name}</LabelText>
          <LabelText label="Invoice Client:">{invoice?.invoice_client?.name}</LabelText>
          <LabelText label="Status:">
            <Badge
                title={invoice?.status ?? ''}
                color={invoiceStatusColors[invoice?.status ?? 'draft']}
            />
          </LabelText>
          <LabelText label="Issue On:">
            {invoice?.issue_on ? dateToUTC(new Date(invoice?.issue_on), 'MM/dd/yyyy') : 'N/A'}
          </LabelText>
          <LabelText label="Due On:">
            {invoice?.due_on ? dateToUTC(new Date(invoice?.due_on), 'MM/dd/yyyy') : 'N/A'}
          </LabelText>
          <LabelText label="Sent At:">
            {invoice?.sent_at ? dateToUTC(new Date(invoice?.sent_at), 'MM/dd/yyyy') : 'N/A'}
          </LabelText>
          <LabelText label="Paid At:">
            {invoice?.paid_at ? dateToUTC(new Date(invoice?.paid_at), 'MM/dd/yyyy') : 'N/A'}
          </LabelText>
          <LabelText label="Created At:">
            {invoice?.created_at
                ? formatDate(new Date(invoice?.created_at), 'MM/dd/yyyy h:mm a')
                : 'N/A'}
          </LabelText>
          <LabelText label="Total:"><CurrencyCell value={invoice?.total ?? 0} /></LabelText>          {userCanAdmin_Invoices(currentUser) && (
            <>
              <IconLink
                  icon={<BsFiletypePdf size={24} />}
                  text="Preview the invoice PDF"
                  onclick={handlePreviewInvoicePDF}
              />
              <IconLink
                  icon={<BsCreditCard size={24} />}
                  text="Visit the client payment portal"
                  url={`/admin/finance/invoice_client/${invoice?.invoice_client_id}`}
              />
              <IconLink
                  icon={<BsCashCoin size={24} />}
                  text="Make a manual payment"
                  url={`/admin/finance/payments/new?invoice_id=${invoice?.id}`}
              />
            </>
        )}
        </DetailSection>

        <DetailSection title="Contacts">
          {contactManageLinks.map((link) => (
              <LinkCell
                  key={link.url}
                  content={link.label}
                  url={link.url}
                  state={{ returnTo: `/admin/finance/invoices/${invoice?.id}` }}
                  underline
              />
          ))}
          {invoiceContacts.length > 0 ? (
              invoiceContacts.map((contact) => (
                      <Form.Check
                          key={contact.id}
                          type="checkbox"
                          id={String(contact.id)}
                          checked={checkedValues.includes(String(contact.id))}
                          label={contact.email}
                          onChange={handleCheckboxChange}
                      />
                  ))
          ) : (
              <span className="fst-italic">There are no contacts to show.</span>
          )}

          {invoiceContacts.length > 0 && (
              <>
                <Button
                    variant="warning"
                    onClick={sendInvoiceHandler}
                    disabled={disabledButton}
                >
                  Send invoice
                </Button>
                <Button
                    variant="danger"
                    onClick={warnInvoiceHandler}
                    disabled={disabledButton}
                >
                  Send Past Due Notice
                </Button>
              </>
          )}
        </DetailSection>

        <DetailSection title="Organization Notes">
          {uniqBy(
              invoice?.courses?.flatMap((c) => c.venue.organizations) ?? [],
              'id'
          ).map((o, idx) => (
              <LabelTextList
                  key={idx}
                  label={`${o.name} Notes`}
                  list={[o.notes ?? 'No notes']}
              />
          ))}
        </DetailSection>

        <DetailSection title="Venue Notes">
          {uniqBy(invoice?.courses ?? [], 'venue_id').map((c, idx) => (
              <LabelTextList
                  key={idx}
                  label={`${c.venue.title} Notes`}
                  list={[c.venue.notes ?? 'No notes']}
              />
          ))}
        </DetailSection>
      </>
  );

  return (
      <>
        <div
            className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
            style={{ background: '#EFEFEF' }}
        >
          <Stack
              direction="horizontal"
              gap={2}
              className="ps-lg-3 ps-0"
          >
            <button
                className="btn btn-link text-info p-0"
                onClick={() => navigate('/admin/finance/invoices')}
            >
              <FaArrowLeft
                  size={20}
                  className="d-lg-none"
              />
              <FaArrowLeft
                  size={24}
                  className="d-none d-lg-block"
              />
            </button>
            <h2
                className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
                style={{ color: '#888888', fontSize: '1rem' }}
            >
              Invoices List
            </h2>
            <h2 className="d-none d-lg-block text-info fw-bold mb-0">{invoice?.name}</h2>
          </Stack>

          {userCanAdmin_Invoices(currentUser) && (
              <Stack
                  direction="horizontal"
                  gap={1}
                  className="d-none d-lg-flex"
              >
                <SimpleButton
                    variant="primary"
                    height={36}
                    onClick={() => navigate(`/admin/finance/invoice-items/new?invoice=${invoice?.id}`)}
                >
                  Add Invoice Item
                </SimpleButton>
                <SimpleButton
                    variant="primary"
                    height={36}
                    onClick={() => navigate(`/admin/finance/invoices/${invoice?.id}/edit`)}
                >
                  Edit invoice
                </SimpleButton>
                <SimpleButton
                    variant="primary"
                    height={36}
                    onClick={() => setShowModal(true)}
                >
                  Delete invoice
                </SimpleButton>
              </Stack>
          )}
        </div>
        <div className="d-lg-none">
          <Stack
              gap={4}
              className="p-4"
          >
            {invoiceDetails}
            {accordion}
          </Stack>
        </div>
        <div className="d-none d-lg-flex">
          <div
              className="w-100 mt-5 px-5 py-4"
              style={{ maxWidth: 'calc(100% - 400px)' }}
          >
            {accordion}
          </div>
          <Stack
              gap={4}
              className="vh-100 p-4"
              style={{ width: 400, background: '#F2F2F2' }}
          >
            {invoiceDetails}
          </Stack>
        </div>

        <ConfirmationModal
            show={showModal}
            title="Delete Invoice"
            message="Are you sure you want to remove this invoice?"
            confirmText="Yes, Delete"
            cancelText="Cancel"
            onConfirm={handleConfirmDelete}
            onCancel={() => setShowModal(false)}
        />
      </>
  );
};